<template>
  <div class="m-5 flex-grow safe-area-inset-padding-top relative">
    <klereo-banner
      @left="router.push('/')"
      left-icon="arrow-left"
      @right="scanMutation.mutate"
      right-icon="refresh"
      :right-class="isPending ? 'animate-spin pointer-events-none' : ''"
      :title="$t('BleBluetoothTitle')"
      @title-click="devModeHandler"
      :description="$t('BleScanTitle')"
      ><div class="hidden lg:flex items-center">
        <router-link to="/"
          ><klereo-icon name="house" class="w-[22px]"
        /></router-link></div
    ></klereo-banner>

    <div>
      <div class="flex flex-col gap-6">
        <BlePublicViewAlert />
        <div class="flex flex-col gap-2">
          <div class="text-gray-200 text-sm">
            {{ $t("BleAvailablePoolsTitle") }}
          </div>

          <div class="flex flex-col gap-2">
            <BleScanResult
              v-for="device of devices"
              :key="device.device.deviceId"
              :result="device"
            />
          </div>

          <div
            v-if="isSuccess && devices.length === 0"
            class="text-white text-center"
          >
            {{ $t("BleScanNoResultText") }}
          </div>

          <div class="text-center">
            <MutationWrapper :mutation="scanMutation" />
          </div>
        </div>

        <OfflinePoolsList :excluded-device-ids="excludedDeviceIds" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import KlereoBanner from "@/shared/components/KlereoBanner.vue";
import KlereoIcon from "@/shared/components/KlereoIcon.vue";
import { computed, onMounted, ref } from "vue";
import { BleClient, ScanResult } from "@capacitor-community/bluetooth-le";
import { requestLEScan } from "@/features/ble/utils/requestLEScan.ts";
import { lastValueFrom, tap } from "rxjs";
import { useMutation } from "@tanstack/vue-query";
import { useStoredBlePools } from "@/features/ble/utils/localStorage.ts";
import { useRouter } from "vue-router";
import BleScanResult from "@/features/ble/scan/BleScanResult.vue";
import MutationWrapper from "@/shared/components/MutationWrapper.vue";
import OfflinePoolsList from "@/features/ble/public/OfflinePoolsList.vue";
import { useDevMode } from "@/shared/mixins/useDevMode.ts";
import { addIcons } from "oh-vue-icons";
import {
  MdSignalwifistatusbar1BarTwotone,
  MdSignalwifistatusbar2BarTwotone,
  MdSignalwifistatusbar3BarTwotone,
  MdSignalwifistatusbar4Bar,
  MdSignalwifistatusbarnotconnectedTwotone,
} from "oh-vue-icons/icons";
import BlePublicViewAlert from "@/features/ble/public/BlePublicViewAlert.vue";

const router = useRouter();

const storedPools = useStoredBlePools();
const devices = ref([] as ScanResult[]);

const addResult = (result: ScanResult) => {
  const existingIdx = devices.value.findIndex(
    (device) => device.device.deviceId === result.device.deviceId
  );
  if (existingIdx !== -1) {
    devices.value[existingIdx] = result;
  } else {
    if (
      storedPools.value.length === 1 &&
      result.device.deviceId === storedPools.value[0].deviceId &&
      storedPools.value[0].autoConnect
    ) {
      BleClient.stopLEScan();
      router.push({
        path: `/pool/ble:${result.device.deviceId}`,
      });
    }
    devices.value.push(result);
  }
};

const scanMutation = useMutation({
  mutationFn: async () => {
    devices.value = [];
    return lastValueFrom(requestLEScan().pipe(tap(addResult)), {
      defaultValue: null,
    });
  },
});

const excludedDeviceIds = computed(() =>
  devices.value.map((d) => d.device.deviceId)
);

const { isPending, isSuccess } = scanMutation;

const { handler: devModeHandler } = useDevMode();

onMounted(scanMutation.mutate);

addIcons(
  MdSignalwifistatusbarnotconnectedTwotone,
  MdSignalwifistatusbar1BarTwotone,
  MdSignalwifistatusbar2BarTwotone,
  MdSignalwifistatusbar3BarTwotone,
  MdSignalwifistatusbar4Bar
);
</script>
